import { Toast, ToastActionButton } from '@heidi/design-system/toast';
import { cn } from '@heidi/utils';
import { type ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import type { DictationFeedbackAnalyticsPorts } from '../../contracts/dictation-feedback.types';
import { trackFeedbackAnalyticsSafely } from '../../data/analytics/trackFeedbackAnalyticsSafely';
import { getDictationAnalyticsPorts } from '../../dictation-analytics-ports';
import { dictationFeedbackConfirmationMessages as messages } from './dictation-feedback-confirmation.messages';

type DictationFeedbackConfirmationSurface = Parameters<
  DictationFeedbackAnalyticsPorts['trackSeeHistoryClicked']
>[0];

interface DictationFeedbackConfirmationProps extends Omit<
  ComponentProps<typeof Toast>,
  'action' | 'description' | 'onDismiss' | 'title' | 'tone'
> {
  onDismiss: () => void;
  onSeeHistory: () => void;
  surface: DictationFeedbackConfirmationSurface;
}

/** Shared post-submit acknowledgement used by both Dictate feedback surfaces. */
export const DictationFeedbackConfirmation = ({
  className,
  onDismiss,
  onSeeHistory,
  surface,
  ...props
}: DictationFeedbackConfirmationProps) => {
  const handleSeeHistory = () => {
    trackFeedbackAnalyticsSafely('see_history_clicked', () =>
      getDictationAnalyticsPorts().feedback.trackSeeHistoryClicked(surface)
    );
    onSeeHistory();
  };

  return (
    <Toast
      role="status"
      data-testid="dictation-feedback-confirmation"
      data-widget-hit-surface
      tone="success"
      title={<FormattedMessage {...messages.title} />}
      description={<FormattedMessage {...messages.description} />}
      action={
        <ToastActionButton tone="success" onClick={handleSeeHistory}>
          <FormattedMessage {...messages.seeHistory} />
        </ToastActionButton>
      }
      onDismiss={onDismiss}
      className={cn('shrink-0', className)}
      {...props}
    />
  );
};
